import { IItemTagsClient } from './IItemTagsClient';
import {
  ItemTagAssignablePage,
  ItemTagAssignableItem,
  ItemTagAssignmentContext,
  ItemTagAssignmentFilters,
  ItemTagDto,
  ItemTagListFilters,
  ItemTagUpsertDto,
} from './itemTagTypes';
import { client } from '../orderManagementClient';

type ApiEnvelope<T> = {
  data?: T;
  success?: boolean;
  message?: string;
};

export class ItemTagsClientApi implements IItemTagsClient {
  private readonly baseUrl: string;

  constructor(baseUrl: string = '/api/item-tags') {
    this.baseUrl = baseUrl;
  }

  private async getHeaders(): Promise<HeadersInit> {
    const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });

    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    try {
      const errorData = await response.json();
      return errorData.message || errorData.detail || errorData.title || fallback;
    } catch {
      return fallback;
    }
  }

  private async readResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response, fallbackMessage));
    }

    const payload = await response.json() as ApiEnvelope<T> | T;

    if ('data' in (payload as ApiEnvelope<T>)) {
      const envelope = payload as ApiEnvelope<T>;

      if (envelope.success === false) {
        throw new Error(envelope.message || fallbackMessage);
      }

      return envelope.data as T;
    }

    return payload as T;
  }

  private readResultData<T>(result: { success?: boolean; message?: string; data?: T }, fallbackMessage: string): T {
    if (!result?.success) {
      throw new Error(result?.message || fallbackMessage);
    }

    return result.data as T;
  }

  async getItemTags(filters?: ItemTagListFilters): Promise<ItemTagDto[]> {
    const headers = await this.getHeaders();
    const params = new URLSearchParams();

    if (filters?.searchText) {
      params.append('searchText', filters.searchText);
    }

    if (filters?.companyId) {
      params.append('companyId', filters.companyId.toString());
    }

    if (filters?.customerTypeId) {
      params.append('customerTypeId', filters.customerTypeId.toString());
    }

    if (filters?.isActive !== undefined) {
      params.append('isActive', filters.isActive.toString());
    }

    params.append('sort', 'name');
    params.append('direction', 'asc');
    params.append('pageNumber', '0');
    params.append('pageSize', '500');

    const url = `${this.baseUrl}?${params.toString()}`;
    const response = await fetch(url, { headers });
    const paged = await this.readResponse<{ items: ItemTagDto[] }>(response, 'Failed to fetch item tags.');
    return paged.items ?? [];
  }

  async getItemTag(id: number): Promise<ItemTagDto | null> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, { headers });

    if (response.status === 404) {
      return null;
    }

    return this.readResponse<ItemTagDto>(response, 'Failed to fetch item tag.');
  }

  async getAssignedItemIds(itemTagId: number): Promise<number[]> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${itemTagId}/items`, { headers });

    return this.readResponse<number[]>(response, 'Failed to fetch assigned items.');
  }

  async createItemTag(dto: ItemTagUpsertDto): Promise<ItemTagDto> {
    const headers = await this.getHeaders();
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(dto),
    });

    const createdId = await this.readResponse<number>(response, 'Failed to create item tag.');
    const createdItemTag = await this.getItemTag(createdId);

    if (!createdItemTag) {
      throw new Error('Created item tag could not be loaded.');
    }

    return createdItemTag;
  }

  async updateItemTag(id: number, dto: ItemTagUpsertDto): Promise<ItemTagDto> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(dto),
    });

    const updated = await this.readResponse<boolean>(response, 'Failed to update item tag.');
    if (!updated) {
      throw new Error('Item tag update was not applied.');
    }

    const updatedItemTag = await this.getItemTag(id);
    if (!updatedItemTag) {
      throw new Error('Updated item tag could not be loaded.');
    }

    return updatedItemTag;
  }

  async deleteItemTag(id: number): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
      headers,
    });

    const deleted = await this.readResponse<boolean>(response, 'Failed to delete item tag.');
    if (!deleted) {
      throw new Error('Item tag delete was not applied.');
    }
  }

  async getAssignableItems(context: ItemTagAssignmentContext, filters?: ItemTagAssignmentFilters): Promise<ItemTagAssignablePage> {
    if (!context.companyId || !context.customerTypeId || !filters?.catalogueId) {
      return { items: [], totalPages: 0, totalCount: 0 };
    }

    // Use catalogueProducts endpoint so items are already scoped to the chosen catalogue
    // with server-side search/category filtering and pagination.
    const result = await client.getCatalogueProducts(filters.catalogueId.toString(), {
      storeNumber: context.storeNumber,
      searchTerm: filters.searchText ?? '',
      category: filters.categoryId ? filters.categoryId.toString() : '',
      pageNumber: filters.pageNumber ?? 0,
      pageSize: filters.pageSize ?? 50,
      sort: '',
      direction: 'desc',
    });

    const products = this.readResultData(result, 'Failed to fetch assignable items.');

    return {
      items: products.items.items.map((item) => ({
        contentId: item.id,
        itemNumber: item.sku,
        productName: item.name,
        categoryId: typeof item.categoryId === 'string' ? Number(item.categoryId) : item.categoryId,
        categoryName: item.category,
        catalogueIds: [],
        isAssigned: false, // actual assignment tracked via persistedAssignedItemIds
      })),
      totalPages: products.items.totalPages,
      totalCount: products.items.totalCount,
    };
  }

  async assignItems(itemTagId: number, contentIds: number[]): Promise<void> {
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseUrl}/${itemTagId}/items`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ contentIds }),
    });

    await this.readResponse<void>(response, 'Failed to assign items to the selected tag.');
  }
}
